import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Trash2, Loader2, Lock, Trophy, Calendar as CalIcon, ArrowUp, ArrowDown, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { buildResolveContext, resolveEventInDay, resolveFinaleHeaderForEvent } from './TornaiEditor';

const inp = 'w-full bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-pink-brand';
const lbl = 'text-white/60 text-[10px] font-bold uppercase tracking-widest mb-1 block';
const Card = ({ children, className = '' }) => <div className={`glass-card p-5 ${className}`}>{children}</div>;
const Loader = () => <div className="text-center py-8"><Loader2 className="text-pink-brand animate-spin mx-auto" /></div>;

/**
 * Gestione completa Classifiche & Risultati per admin/organizzazione.
 *
 * 1) RISULTATI PARTITE — auto-popolati dal calendario (api.getCalendarioFull).
 *    Per ogni evento del calendario c'è un campo "Risultato" che viene salvato
 *    dentro eventi_json del giorno (campo `risultato` dell'evento).
 *
 * 2) CLASSIFICHE FINALI — CRUD completo:
 *    - Aggiungi/Rinomina/Elimina categorie (nessuna è hardcoded)
 *    - Per categoria: ranking con righe (squadra + punti + campi custom)
 *    - Aggiungi colonne custom a una categoria (es. "Vittorie", "Note")
 *    - Riordina e elimina righe
 *    - Salva per categoria (un click salva tutto il blocco)
 */
export default function ClassificheRisultatiManager({ role }) {
  return (
    <div className="space-y-5" data-testid="classifiche-risultati">
      {role === 'organizzazione' && (
        <Card>
          <div className="flex items-start gap-3 text-white/85">
            <Lock className="text-pink-brand flex-shrink-0" size={18} />
            <div className="text-sm">
              <strong>Accesso Organizzazione</strong> — gestisci risultati delle partite e classifiche finali. Le altre sezioni del sito sono gestite dall'Amministratore.
            </div>
          </div>
        </Card>
      )}

      <RisultatiCalendario />
      <ClassificheCRUD />
    </div>
  );
}

/* ============== RISULTATI dal calendario =============== */
function RisultatiCalendario() {
  const [days, setDays] = useState(null);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    api.getCalendarioFull().then((rows) => setDays(Array.isArray(rows) ? rows : [])).catch(() => setDays([]));
  }, []);

  // Contesto risoluzione: ogni volta che i risultati cambiano, ricalcolo standings/winners
  const resolveCtx = useMemo(() => buildResolveContext(days || []), [days]);

  if (!days) return <Loader />;

  const updateEvent = (dayIdx, evIdx, key, val) => {
    setDays((prev) => {
      const next = [...prev];
      const day = { ...next[dayIdx], eventi: [...(next[dayIdx].eventi || [])] };
      day.eventi[evIdx] = { ...day.eventi[evIdx], [key]: val };
      next[dayIdx] = day;
      return next;
    });
  };

  const saveDay = async (day) => {
    setSavingId(day.id);
    try {
      await api.saveCalendario({
        id: day.id,
        data: day.data,
        giorno: day.giorno,
        eventi: day.eventi,
        sort_order: day.sort_order,
        etichetta: day.etichetta,
      });
      toast.success(`Risultati ${day.giorno} salvati`);
    } catch (e) {
      toast.error(e?.message || 'Errore salvataggio');
    }
    setSavingId(null);
  };

  if (days.length === 0) {
    return (
      <Card>
        <h3 className="text-white font-bold mb-2 flex items-center gap-2"><CalIcon size={18} className="text-pink-brand" /> Risultati partite</h3>
        <p className="text-white/60 text-sm">Nessun giorno nel calendario. Chiedi all'Admin di popolare il calendario per gestire i risultati delle partite qui.</p>
      </Card>
    );
  }

  return (
    <Card>
      <h3 className="text-white font-bold mb-3 flex items-center gap-2"><CalIcon size={18} className="text-pink-brand" /> Risultati partite — dal calendario</h3>
      <p className="text-white/60 text-xs mb-4">Ogni evento programmato nel calendario è elencato qui. Inserisci il risultato/punteggio nel campo dedicato e clicca "Salva giornata" alla fine.</p>
      <div className="space-y-4">
        {days.map((day, di) => (
          <div key={day.id || di} className="bg-navy-dark/70 border border-white/10 rounded-lg p-3" data-testid={`risultati-day-${di}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-white font-bold text-sm">{day.giorno} <span className="text-white/60 font-normal">{day.data}</span></div>
              <button
                onClick={() => saveDay(day)}
                disabled={savingId === day.id}
                className="btn-primary !py-1.5 !px-3 !text-xs"
              >
                <Save size={12} /> {savingId === day.id ? 'Salvo…' : 'Salva giornata'}
              </button>
            </div>
            <div className="space-y-2">
              {(day.eventi || []).map((e, ei) => {
                const resolved = resolveEventInDay(e.evento, day, resolveCtx);
                const showEventPreview = resolved && resolved !== e.evento;
                // Se il "giorno" contiene un placeholder (es. "Win. Gir. X - Win. Gir. Y")
                // e l'evento è solo il nome categoria (es. "PRIMI CALCI 2017/18"),
                // risolve il giorno per la categoria specifica dell'evento.
                const finaleHeader = resolveFinaleHeaderForEvent(day.giorno, e.evento, day, resolveCtx);
                return (
                <div key={ei} className="grid sm:grid-cols-12 gap-2 items-center bg-navy/60 p-2 rounded border border-white/5">
                  <div className="sm:col-span-2 text-pink-brand font-bold text-xs">{e.ora}</div>
                  <div className="sm:col-span-5 text-white text-sm leading-tight">
                    {e.evento}
                    {showEventPreview && (
                      <div className="text-pink-brand text-xs mt-1 font-semibold" data-testid="resolved-preview">
                        → {resolved}
                      </div>
                    )}
                    {finaleHeader && (
                      <div className="text-pink-brand text-xs mt-1 font-semibold" data-testid="resolved-finale-header">
                        → Finale: {finaleHeader}
                      </div>
                    )}
                    {e.luogo ? <div className="text-white/50 text-xs mt-0.5">{e.luogo}</div> : null}
                  </div>
                  <div className="sm:col-span-5">
                    <input
                      className={inp + ' !py-1.5'}
                      placeholder="Risultato (es. 3-2, Vince Squadra A, ecc.)"
                      value={e.risultato || ''}
                      onChange={(ev) => updateEvent(di, ei, 'risultato', ev.target.value)}
                    />
                  </div>
                </div>
                );
              })}
              {(day.eventi || []).length === 0 && (
                <div className="text-white/50 text-xs text-center py-2">Nessun evento in questa giornata.</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ============== CLASSIFICHE CRUD =============== */
function ClassificheCRUD() {
  const [list, setList] = useState(null); // array di {categoria, ranking, finale, premi}
  const [newCat, setNewCat] = useState('');

  const reload = () => {
    api.getClassifiche().then((obj) => {
      const arr = Object.values(obj || {});
      setList(arr.map((r) => ({
        categoria: r.categoria || '',
        finale: r.finale || '',
        ranking: r.ranking || [],
        premi: r.premi || [],
      })));
    }).catch(() => setList([]));
  };
  useEffect(reload, []);
  if (!list) return <Loader />;

  const addCategory = async () => {
    const name = newCat.trim();
    if (!name) { toast.error('Inserisci un nome categoria'); return; }
    if (list.some((c) => c.categoria === name)) { toast.error('Categoria già esistente'); return; }
    try {
      await api.saveClassifica({ categoria: name, finale: '', ranking: [], premi: [] });
      setNewCat('');
      reload();
      toast.success('Categoria aggiunta');
    } catch (e) { toast.error(e?.message); }
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-bold flex items-center gap-2"><Trophy size={18} className="text-pink-brand" /> Classifiche finali</h3>
      </div>
      <p className="text-white/60 text-xs mb-3">Per ogni categoria puoi aggiungere righe (squadra + punti) e creare colonne personalizzate (es. "Vittorie", "Pareggi", "Note").</p>

      <div className="bg-navy-dark/60 border border-white/10 rounded-lg p-3 mb-4 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <label className={lbl}>Aggiungi nuova categoria</label>
          <input className={inp} value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="es. Pulcini 2014, Esordienti, Open…" />
        </div>
        <button onClick={addCategory} className="btn-primary !py-2 !px-4 !text-sm"><Plus size={14} /> Aggiungi categoria</button>
      </div>

      <div className="space-y-4">
        {list.map((c) => (
          <CategoryEditor key={c.categoria} cat={c} onSaved={reload} onAfterDelete={reload} />
        ))}
        {list.length === 0 && <p className="text-white/50 text-sm text-center py-4">Nessuna categoria. Aggiungine una qui sopra.</p>}
      </div>
    </Card>
  );
}

function CategoryEditor({ cat, onSaved, onAfterDelete }) {
  const [d, setD] = useState({ ...cat, ranking: cat.ranking || [] });
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(cat.categoria);
  const [saving, setSaving] = useState(false);

  // Lista delle "colonne custom" presenti in almeno una riga (escludendo pos/team)
  const customCols = useMemo(() => {
    const set = new Set();
    (d.ranking || []).forEach((r) => Object.keys(r).forEach((k) => {
      if (!['pos', 'team', 'id'].includes(k)) set.add(k);
    }));
    // Garantisce sempre la colonna "punti"
    set.add('punti');
    return Array.from(set);
  }, [d.ranking]);

  const updateRow = (i, k, v) => {
    setD((p) => {
      const next = { ...p, ranking: [...p.ranking] };
      next.ranking[i] = { ...next.ranking[i], [k]: v };
      return next;
    });
  };
  const addRow = () => {
    setD((p) => ({
      ...p,
      ranking: [...p.ranking, { pos: p.ranking.length + 1, team: '', punti: '' }],
    }));
  };
  const removeRow = (i) => {
    setD((p) => ({ ...p, ranking: p.ranking.filter((_, idx) => idx !== i).map((r, idx) => ({ ...r, pos: idx + 1 })) }));
  };
  const moveRow = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= d.ranking.length) return;
    setD((p) => {
      const next = [...p.ranking];
      [next[i], next[j]] = [next[j], next[i]];
      return { ...p, ranking: next.map((r, idx) => ({ ...r, pos: idx + 1 })) };
    });
  };

  const addCustomColumn = () => {
    const name = window.prompt('Nome del nuovo campo (es. "Vittorie", "Note"):');
    if (!name) return;
    const key = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    if (!key) return;
    if (customCols.includes(key)) { toast.error('Campo già presente'); return; }
    setD((p) => ({
      ...p,
      ranking: p.ranking.map((r) => ({ ...r, [key]: '' })),
    }));
  };
  const removeCustomColumn = (key) => {
    if (key === 'punti' || key === 'team') return;
    if (!window.confirm(`Rimuovere la colonna "${key}" da questa categoria?`)) return;
    setD((p) => ({
      ...p,
      ranking: p.ranking.map((r) => { const c = { ...r }; delete c[key]; return c; }),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      // Se rinominata, eliminiamo la vecchia categoria e ne creiamo una nuova
      const targetName = renaming ? newName.trim() : d.categoria;
      if (!targetName) { toast.error('Nome categoria vuoto'); setSaving(false); return; }
      if (renaming && targetName !== cat.categoria) {
        // Backend non supporta rename diretto: salviamo nuova e cancelliamo vecchia
        await api.saveClassifica({ categoria: targetName, finale: d.finale, ranking: d.ranking, premi: d.premi });
        await api.deleteClassifica(cat.categoria).catch(() => {});
      } else {
        await api.saveClassifica({ categoria: targetName, finale: d.finale, ranking: d.ranking, premi: d.premi });
      }
      setRenaming(false);
      toast.success('Classifica salvata');
      onSaved();
    } catch (e) { toast.error(e?.message || 'Errore salvataggio'); }
    setSaving(false);
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare la categoria "${cat.categoria}" e tutta la sua classifica?`)) return;
    try { await api.deleteClassifica(cat.categoria); onAfterDelete(); }
    catch (e) { toast.error(e?.message); }
  };

  return (
    <div className="bg-navy-dark/70 border border-white/10 rounded-lg p-4" data-testid={`category-${cat.categoria}`}>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          {renaming ? (
            <>
              <input className={inp + ' max-w-xs'} value={newName} onChange={(e) => setNewName(e.target.value)} />
              <button onClick={save} disabled={saving} className="btn-primary !py-1.5 !px-3 !text-xs"><Save size={12} /> Conferma</button>
              <button onClick={() => { setRenaming(false); setNewName(cat.categoria); }} className="text-white/60 text-xs">Annulla</button>
            </>
          ) : (
            <>
              <h4 className="text-white font-bold">{d.categoria}</h4>
              <button onClick={() => setRenaming(true)} className="text-white/60 hover:text-pink-brand p-1" title="Rinomina"><Edit3 size={13} /></button>
            </>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={addRow} className="btn-primary !py-1.5 !px-3 !text-xs"><Plus size={12} /> Riga</button>
          <button onClick={addCustomColumn} className="px-3 py-1.5 rounded-full bg-navy border border-white/15 text-white/80 hover:text-pink-brand hover:border-pink-brand text-[11px] font-bold uppercase">
            <Plus size={11} className="inline" /> Campo
          </button>
          <button onClick={save} disabled={saving} className="btn-primary !py-1.5 !px-3 !text-xs"><Save size={12} /> {saving ? 'Salvo…' : 'Salva'}</button>
          <button onClick={remove} className="px-3 py-1.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-[11px] font-bold uppercase">Elimina</button>
        </div>
      </div>

      {d.ranking.length === 0 ? (
        <p className="text-white/50 text-sm text-center py-3">Classifica vuota — clicca "Riga" per aggiungere una squadra.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-white/60 text-[10px] uppercase tracking-widest border-b border-white/10">
                <th className="px-2 py-2 text-left w-12">#</th>
                <th className="px-2 py-2 text-left">Squadra</th>
                {customCols.map((col) => (
                  <th key={col} className="px-2 py-2 text-left">
                    <span className="inline-flex items-center gap-1">
                      {col}
                      {col !== 'punti' && col !== 'team' && (
                        <button onClick={() => removeCustomColumn(col)} className="text-white/40 hover:text-red-400" title="Rimuovi colonna"><Trash2 size={10} /></button>
                      )}
                    </span>
                  </th>
                ))}
                <th className="px-2 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {d.ranking.map((r, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="px-2 py-1.5">
                    <span className="w-7 h-7 rounded-full bg-pink-brand text-white font-bold text-xs flex items-center justify-center">{r.pos}</span>
                  </td>
                  <td className="px-2 py-1.5">
                    <input className={inp + ' !py-1'} value={r.team || ''} onChange={(e) => updateRow(i, 'team', e.target.value)} placeholder="Nome squadra" />
                  </td>
                  {customCols.map((col) => (
                    <td key={col} className="px-2 py-1.5">
                      <input className={inp + ' !py-1'} value={r[col] || ''} onChange={(e) => updateRow(i, col, e.target.value)} placeholder={col} />
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-right">
                    <div className="inline-flex">
                      <button onClick={() => moveRow(i, -1)} disabled={i === 0} className="p-1 text-white/60 hover:text-pink-brand disabled:opacity-30"><ArrowUp size={13} /></button>
                      <button onClick={() => moveRow(i, 1)} disabled={i === d.ranking.length - 1} className="p-1 text-white/60 hover:text-pink-brand disabled:opacity-30"><ArrowDown size={13} /></button>
                      <button onClick={() => removeRow(i)} className="p-1 text-white/60 hover:text-red-400"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <div>
          <label className={lbl}>Finale (testo libero)</label>
          <input className={inp} value={d.finale || ''} onChange={(e) => setD((p) => ({ ...p, finale: e.target.value }))} placeholder="es. Squadra A — Squadra B 3-2" />
        </div>
        <div>
          <label className={lbl}>Premi / Note (uno per riga)</label>
          <textarea rows={2} className={inp} value={(d.premi || []).join('\n')} onChange={(e) => setD((p) => ({ ...p, premi: e.target.value.split('\n') }))} />
        </div>
      </div>
    </div>
  );
}
